import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { getAuthConfig } from '../utils/auth';
import ScaleToFit from './ScaleToFit';
import { captureCard } from '../utils/captureCard';
import { defaultLocalDateTimeInput, localDateTimeToUtcIso, utcIsoToLocalDateTimeInput } from '../utils/datetime';

const DEFAULT_RELEASE_DATE = () => defaultLocalDateTimeInput(7);

const SUPPORTED_VIDEO_EXTENSIONS = new Set([
  '.mp4',
  '.m4v',
  '.mov',
  '.webm',
  '.avi',
  '.mkv',
  '.wmv',
  '.mpeg',
  '.mpg',
  '.ts',
  '.m2ts',
  '.mts',
  '.3gp',
  '.3g2',
  '.ogv',
  '.ogg',
  '.mxf',
]);

const ACCEPTED_VIDEO_INPUT = `${Array.from(SUPPORTED_VIDEO_EXTENSIONS).join(',')},video/*`;

function isSupportedVideoFile(file) {
  if (!file) {
    return false;
  }

  const mimeType = (file.type || '').toLowerCase();
  const lowerName = (file.name || '').toLowerCase();

  if (mimeType.startsWith('video/')) {
    return true;
  }

  return Array.from(SUPPORTED_VIDEO_EXTENSIONS).some((extension) => lowerName.endsWith(extension));
}

function formatBytes(bytes) {
  if (!bytes) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;

  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

function VideoAccessCard({ qrId, qrUrl, video, senderName }) {
  return (
    <ScaleToFit width={980} height={560}>
    <div
      id={`video-card-${qrId}`}
      style={{
        background: 'linear-gradient(135deg, #f5f1e8 0%, #fffbf5 100%)',
        border: '3px solid #8b7355',
        borderRadius: '0px',
        padding: '36px 40px',
        boxShadow: '0 15px 40px rgba(0, 0, 0, 0.35)',
        color: '#2d2d2d',
        fontFamily: 'Georgia, serif',
        position: 'relative',
        minHeight: '560px',
        width: '100%',
        maxWidth: '980px',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '36px',
          bottom: '36px',
          width: '2px',
          background: '#8b7355',
        }}
      />

      <div
        style={{
          position: 'absolute',
          left: '40px',
          top: '36px',
          bottom: '36px',
          width: 'calc(50% - 42px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingRight: '24px',
        }}
      >
        <div
          style={{
            background: 'white',
            padding: '15px',
            border: '3px solid #999',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 5px 15px rgba(0,0,0,0.25)',
            marginTop: '24px',
          }}
        >
          <QRCodeSVG value={qrUrl} size={300} level="H" includeMargin={false} />
        </div>

        <div
          style={{
            fontSize: '12px',
            color: '#555',
            fontStyle: 'italic',
            lineHeight: '1.5',
            textAlign: 'center',
          }}
        >
          <strong>Hinweis:</strong> QR-Code mit Kamera scannen und die Videobotschaft spaeter sicher abrufen.
        </div>
      </div>

      <div
        style={{
          marginLeft: 'calc(50% + 28px)',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
          minHeight: '488px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div
            style={{
              fontSize: '56px',
              fontWeight: 'normal',
              color: '#8b5a3c',
              letterSpacing: '4px',
              fontStyle: 'italic',
              fontFamily: "'Brush Script MT', 'Great Vibes', cursive",
              lineHeight: '1.0',
            }}
          >
            VideoCARD
          </div>
          <div
            style={{
              width: '92px',
              height: '102px',
              border: '2px solid #8b7355',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
            }}
          >
            🎥
          </div>
        </div>

        <p style={{ margin: 0, color: '#8b5a3c', fontStyle: 'italic' }}>Von: {senderName}</p>
        {video.release_date && (
          <p style={{ margin: 0, marginTop: '-10px', fontSize: '13px', color: '#8b5a3c', fontStyle: 'italic' }}>
            📅 Freigabe: {new Date(video.release_date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        )}
        <p
          style={{
            margin: 0,
            paddingBottom: '8px',
            borderBottom: '2px solid #2d2d2d',
            display: 'inline-block',
            fontWeight: '700',
            color: '#2d2d2d',
            fontStyle: 'italic',
          }}
        >
          Fuer {video.recipient_name}
        </p>

        {video.message_image ? (
          <img
            src={video.message_image}
            alt="Video Vorschau"
            style={{
              width: '100%',
              maxHeight: '220px',
              objectFit: 'cover',
              borderRadius: '10px',
              border: '1px solid rgba(139, 115, 85, 0.45)',
            }}
          />
        ) : (
          <div
            style={{
              minHeight: '220px',
              borderRadius: '10px',
              border: '1px dashed rgba(139, 115, 85, 0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#8b5a3c',
              fontSize: '18px',
              fontWeight: '600',
            }}
          >
            Videobotschaft freischalten
          </div>
        )}

        <div style={{ fontSize: '15px', lineHeight: '1.7', color: '#2d2d2d' }}>
          {video.message_text || 'Diese Videobotschaft wurde fuer den richtigen Moment hinterlegt.'}
        </div>

        <div style={{ marginTop: 'auto', fontSize: '13px', color: '#555' }}>
          Freigabe: {video.release_date ? new Date(video.release_date).toLocaleString('de-DE') : 'sofort'}
          {video.event_name ? ` | Event: ${video.event_name}` : ''}
        </div>
      </div>
    </div>
    </ScaleToFit>
  );
}

function Videos({ user }) {
  const fileInputRef = useRef(null);
  const [videos, setVideos] = useState([]);
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [currentCardId, setCurrentCardId] = useState('main');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    recipient_name: '',
    message_text: '',
    release_date: DEFAULT_RELEASE_DATE(),
    event_id: '',
    video_file: null,
  });
  const [editFormData, setEditFormData] = useState({
    recipient_name: '',
    message_text: '',
    release_date: '',
    event_id: '',
  });

  const hasVideoAccess = useMemo(
    () => new Set(['family', 'family-plus', 'jga-plus']).has(user?.plan),
    [user?.plan]
  );

  useEffect(() => {
    if (!hasVideoAccess) {
      return;
    }

    loadVideos();
    loadEvents();
  }, [hasVideoAccess]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const loadVideos = async () => {
    try {
      const response = await axios.get('/api/videos', getAuthConfig());
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Failed to load videos:', error);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await axios.get('/api/events', getAuthConfig());
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const resetCreateForm = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setPreviewUrl(null);
    setDragOver(false);
    setErrors({});
    setFormData({
      recipient_name: '',
      message_text: '',
      release_date: DEFAULT_RELEASE_DATE(),
      event_id: '',
      video_file: null,
    });
  };

  const startEdit = (video) => {
    setSelectedVideo(video);
    setEditingVideo(video.id);
    setEditFormData({
      recipient_name: video.recipient_name || '',
      message_text: video.message_text || '',
      release_date: utcIsoToLocalDateTimeInput(video.release_date),
      event_id: video.event_id || '',
    });
  };

  const handleVideoSelected = (file) => {
    if (!file) {
      return;
    }

    if (!isSupportedVideoFile(file)) {
      alert('Bitte ein unterstuetztes Videoformat auswaehlen.');
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setErrors((current) => ({
      ...current,
      video_file: false,
    }));
    setPreviewUrl(URL.createObjectURL(file));
    setFormData((current) => ({
      ...current,
      video_file: file,
    }));
  };

  const handleVideoDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    handleVideoSelected(event.dataTransfer.files?.[0]);
  };

  const handleCreateVideo = async (event) => {
    event.preventDefault();

    const nextErrors = {};

    if (!formData.recipient_name.trim()) {
      nextErrors.recipient_name = true;
    }

    if (!formData.video_file) {
      nextErrors.video_file = true;
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const payload = new FormData();
      payload.append('recipient_name', formData.recipient_name.trim());
      payload.append('message_text', formData.message_text || '');
      payload.append('release_date', localDateTimeToUtcIso(formData.release_date) || '');
      payload.append('event_id', formData.event_id || '');
      payload.append('video', formData.video_file);

      const response = await axios.post('/api/videos/create', payload, getAuthConfig());
      const token = response.data.token;
      const createdVideo = response.data.video;

      setQrData({
        ...createdVideo,
        event_name: response.data.linked_event?.eventName || createdVideo.event_name || '',
        url: `${window.location.origin}/view/${token}`,
      });
      setShowForm(false);
      resetCreateForm();
      await loadVideos();
    } catch (error) {
      alert(error.response?.data?.error || 'Video konnte nicht erstellt werden');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();

    try {
      await axios.put(
        `/api/videos/${editingVideo}`,
        {
          recipient_name: editFormData.recipient_name,
          message_text: editFormData.message_text,
          release_date: localDateTimeToUtcIso(editFormData.release_date) || null,
          event_id: editFormData.event_id || null,
        },
        getAuthConfig()
      );

      setEditingVideo(null);
      await loadVideos();
    } catch (error) {
      alert(error.response?.data?.error || 'Video konnte nicht aktualisiert werden');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Videobotschaft wirklich loeschen?')) {
      return;
    }

    try {
      await axios.delete(`/api/videos/${videoId}`, getAuthConfig());
      if (selectedVideo?.id === videoId) {
        setSelectedVideo(null);
        setEditingVideo(null);
      }
      await loadVideos();
    } catch (error) {
      alert(error.response?.data?.error || 'Video konnte nicht geloescht werden');
    }
  };

  const downloadCard = async (cardId) => {
    try {
      const element = document.querySelector(`#video-card-${cardId || currentCardId}`);
      if (!element) {
        alert('Videokarte nicht gefunden');
        return;
      }

      const canvas = await captureCard(element);

      canvas.toBlob((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `timelock_video_postkarte_${Date.now()}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
      }, 'image/png', 1.0);
    } catch (error) {
      alert(`Download fehlgeschlagen: ${error.message}`);
    }
  };

  if (!hasVideoAccess) {
    return (
      <div className="tab-panel">
        <h2>Videobotschaften</h2>
        <p>Dein aktuelles Paket enthaelt noch keinen Video-Reiter.</p>
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <h2>Videobotschaften</h2>
      <p style={{ color: '#94a3b8', marginBottom: '20px' }}>
        Videos werden beim Upload automatisch fuer eine kleinere Dateigroesse optimiert und koennen optional mit einem Event verknuepft werden.
      </p>

      {!qrData ? (
        <>
          <div style={{ marginBottom: '24px' }}>
            {!showForm ? (
              <button
                onClick={() => setShowForm(true)}
                style={{
                  padding: '10px 20px',
                  background: '#a855f7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                + Neue Videobotschaft
              </button>
            ) : (
              <form
                onSubmit={handleCreateVideo}
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  borderRadius: '10px',
                  padding: '20px',
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#94a3b8', marginBottom: '6px', fontSize: '12px' }}>
                    Empfaenger Name *
                  </label>
                  <input
                    type="text"
                    value={formData.recipient_name}
                    onChange={(event) => {
                      const value = event.target.value;
                      setFormData({ ...formData, recipient_name: value });
                      if (value.trim()) {
                        setErrors((current) => ({
                          ...current,
                          recipient_name: false,
                        }));
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#0f172a',
                      border: errors.recipient_name
                        ? '1px solid #ef4444'
                        : '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      boxSizing: 'border-box',
                    }}
                  />
                  {errors.recipient_name && (
                    <div style={{ color: '#fca5a5', fontSize: '11px', marginTop: '6px' }}>
                      Bitte gib einen Empfaenger-Namen ein.
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#94a3b8', marginBottom: '6px', fontSize: '12px' }}>
                    Videobotschaft *
                  </label>
                  <div
                    style={{
                      border: `2px dashed ${
                        errors.video_file
                          ? '#ef4444'
                          : dragOver
                            ? '#c084fc'
                            : 'rgba(168, 85, 247, 0.5)'
                      }`,
                      borderRadius: '8px',
                      padding: '30px 20px',
                      textAlign: 'center',
                      background: errors.video_file
                        ? 'rgba(239, 68, 68, 0.08)'
                        : dragOver
                          ? 'rgba(168, 85, 247, 0.15)'
                          : 'rgba(168, 85, 247, 0.05)',
                      cursor: 'pointer',
                      position: 'relative',
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleVideoDrop}
                  >
                    <div style={{ color: '#a855f7', fontSize: '16px', marginBottom: '8px' }}>
                      Dateien hier hineinziehen oder per Klick auswaehlen
                    </div>
                    <label
                      htmlFor="video-upload"
                      style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        background: '#a855f7',
                        color: 'white',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '12px',
                      }}
                    >
                      Datei auswaehlen
                    </label>
                    <input
                      ref={fileInputRef}
                      id="video-upload"
                      type="file"
                      accept={ACCEPTED_VIDEO_INPUT}
                      onChange={(event) => handleVideoSelected(event.target.files?.[0])}
                      style={{ display: 'none' }}
                    />
                    <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '10px' }}>
                      Unterstuetzt: MP4, MOV, M4V, WEBM, MKV, AVI, WMV, MPEG, TS, 3GP, OGV, MXF
                    </div>
                    <div style={{ color: '#cbd5e1', fontSize: '12px', marginTop: '8px' }}>
                      {formData.video_file
                        ? `${formData.video_file.name} (${formatBytes(formData.video_file.size)})`
                        : 'Noch keine Datei ausgewaehlt'}
                    </div>
                    {errors.video_file && (
                      <div style={{ color: '#fca5a5', fontSize: '11px', marginTop: '8px' }}>
                        Bitte lade eine Videodatei hoch.
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#94a3b8', marginBottom: '6px', fontSize: '12px' }}>
                    Begleittext
                  </label>
                  <textarea
                    value={formData.message_text}
                    onChange={(event) => setFormData({ ...formData, message_text: event.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#0f172a',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      minHeight: '100px',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#94a3b8', marginBottom: '6px', fontSize: '12px' }}>
                    Optional mit Event koppeln
                  </label>
                  <select
                    value={formData.event_id}
                    onChange={(event) => setFormData({ ...formData, event_id: event.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#0f172a',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">Keine Event-Verknuepfung</option>
                    {events.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#94a3b8', marginBottom: '6px', fontSize: '12px' }}>
                    Freigabedatum
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.release_date}
                    onChange={(event) => setFormData({ ...formData, release_date: event.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#0f172a',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '6px',
                      color: '#e2e8f0',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {previewUrl && (
                  <div style={{ marginBottom: '12px' }}>
                    <p style={{ color: '#94a3b8', fontSize: '11px', marginBottom: '8px' }}>
                      Vorschau:
                    </p>
                    <video
                      controls
                      muted
                      src={previewUrl}
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        border: '1px solid rgba(168, 85, 247, 0.3)',
                        background: '#020617',
                      }}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: loading ? '#6b7280' : '#a855f7',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loading ? 'Komprimiere und speichere...' : 'Videobotschaft erstellen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      resetCreateForm();
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: 'transparent',
                      color: '#a855f7',
                      border: '1px solid rgba(168, 85, 247, 0.5)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            )}
          </div>

          <div style={{ display: 'grid', gap: '10px' }}>
            {videos.length === 0 && (
              <div
                style={{
                  background: 'rgba(30, 41, 59, 0.4)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: '10px',
                  padding: '18px',
                  color: '#94a3b8',
                }}
              >
                Noch keine Videobotschaften vorhanden.
              </div>
            )}

            {videos.map((video) => (
              <div key={video.id}>
                <div
                  onClick={() => {
                    setSelectedVideo(selectedVideo?.id === video.id ? null : video);
                    setEditingVideo(null);
                  }}
                  style={{
                    background:
                      selectedVideo?.id === video.id
                        ? 'rgba(168, 85, 247, 0.15)'
                        : 'rgba(30, 41, 59, 0.5)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    borderRadius: '8px',
                    padding: '12px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                    {video.message_image ? (
                      <img
                        src={video.message_image}
                        alt="Video Poster"
                        style={{
                          width: '96px',
                          height: '64px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                          border: '1px solid rgba(148, 163, 184, 0.25)',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '96px',
                          height: '64px',
                          borderRadius: '6px',
                          border: '1px dashed rgba(148, 163, 184, 0.25)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#a855f7',
                          background: 'rgba(15, 23, 42, 0.7)',
                        }}
                      >
                        Video
                      </div>
                    )}

                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#a855f7', fontWeight: '600' }}>{video.recipient_name}</div>
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                        {video.message_text || 'Videobotschaft ohne Begleittext'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                        Freigabe: {video.release_date ? new Date(video.release_date).toLocaleString('de-DE') : 'sofort'}
                        {video.event_name ? ` | Event: ${video.event_name}` : ''}
                      </div>
                    </div>
                  </div>
                </div>

                {selectedVideo?.id === video.id && (
                  <div style={{ marginTop: '8px' }}>
                    {editingVideo === video.id ? (
                      <form
                        onSubmit={handleSaveEdit}
                        style={{
                          background: 'rgba(30, 41, 59, 0.5)',
                          border: '1px solid rgba(168, 85, 247, 0.2)',
                          borderRadius: '8px',
                          padding: '15px',
                        }}
                      >
                        <input
                          type="text"
                          value={editFormData.recipient_name}
                          onChange={(event) => setEditFormData({ ...editFormData, recipient_name: event.target.value })}
                          style={{
                            width: '100%',
                            padding: '8px',
                            background: '#0f172a',
                            border: '1px solid rgba(168, 85, 247, 0.3)',
                            borderRadius: '4px',
                            color: '#e2e8f0',
                            boxSizing: 'border-box',
                            marginBottom: '8px',
                          }}
                        />
                        <textarea
                          value={editFormData.message_text}
                          onChange={(event) => setEditFormData({ ...editFormData, message_text: event.target.value })}
                          style={{
                            width: '100%',
                            padding: '8px',
                            background: '#0f172a',
                            border: '1px solid rgba(168, 85, 247, 0.3)',
                            borderRadius: '4px',
                            color: '#e2e8f0',
                            minHeight: '80px',
                            boxSizing: 'border-box',
                            marginBottom: '8px',
                          }}
                        />
                        <select
                          value={editFormData.event_id}
                          onChange={(event) => setEditFormData({ ...editFormData, event_id: event.target.value })}
                          style={{
                            width: '100%',
                            padding: '8px',
                            background: '#0f172a',
                            border: '1px solid rgba(168, 85, 247, 0.3)',
                            borderRadius: '4px',
                            color: '#e2e8f0',
                            boxSizing: 'border-box',
                            marginBottom: '8px',
                          }}
                        >
                          <option value="">Keine Event-Verknuepfung</option>
                          {events.map((entry) => (
                            <option key={entry.id} value={entry.id}>
                              {entry.name}
                            </option>
                          ))}
                        </select>
                        <input
                          type="datetime-local"
                          value={editFormData.release_date}
                          onChange={(event) => setEditFormData({ ...editFormData, release_date: event.target.value })}
                          style={{
                            width: '100%',
                            padding: '8px',
                            background: '#0f172a',
                            border: '1px solid rgba(168, 85, 247, 0.3)',
                            borderRadius: '4px',
                            color: '#e2e8f0',
                            boxSizing: 'border-box',
                            marginBottom: '8px',
                          }}
                        />

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="submit"
                            style={{
                              flex: 1,
                              padding: '8px',
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            Speichern
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingVideo(null)}
                            style={{
                              flex: 1,
                              padding: '8px',
                              background: '#6b7280',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            Abbrechen
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div
                        style={{
                          background: 'rgba(30, 41, 59, 0.5)',
                          border: '1px solid rgba(168, 85, 247, 0.2)',
                          borderRadius: '8px',
                          padding: '15px',
                        }}
                      >
                        <video
                          controls
                          poster={video.message_image || undefined}
                          style={{
                            width: '100%',
                            borderRadius: '8px',
                            marginBottom: '12px',
                            background: '#020617',
                          }}
                        >
                          <source src={video.message_video} type="video/mp4" />
                        </video>

                        {video.message_text && (
                          <p style={{ color: '#e2e8f0', whiteSpace: 'pre-wrap', marginBottom: '12px' }}>
                            {video.message_text}
                          </p>
                        )}

                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => startEdit(video)}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '120px',
                              padding: '8px 14px',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                            }}
                          >
                            Bearbeiten
                          </button>
                          <button
                            onClick={() => handleDeleteVideo(video.id)}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '120px',
                              padding: '8px 14px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                            }}
                          >
                            Loeschen
                          </button>
                          <button
                            onClick={() => setCurrentCardId(`video_${video.id}`)}
                            style={{
                              flex: '1 1 auto',
                              minWidth: '120px',
                              padding: '8px 14px',
                              background: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                            }}
                          >
                            QR / Link
                          </button>
                        </div>

                        {currentCardId === `video_${video.id}` && (
                          <div
                            style={{
                              marginTop: '12px',
                              background: 'rgba(15, 23, 42, 0.6)',
                              border: '1px solid rgba(168, 85, 247, 0.3)',
                              borderRadius: '8px',
                              padding: '14px',
                            }}
                          >
                            <VideoAccessCard
                              qrId={`video_${video.id}`}
                              qrUrl={`${window.location.origin}/view/${video.token}`}
                              video={video}
                              senderName={user.email?.split('@')[0] || 'admin'}
                            />

                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                              <button
                                onClick={() => {
                                  setCurrentCardId(`video_${video.id}`);
                                  downloadCard(`video_${video.id}`);
                                }}
                                style={{
                                  flex: '1 1 auto',
                                  minWidth: '120px',
                                  padding: '8px 12px',
                                  background: '#a855f7',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                }}
                              >
                                Postkarte downloaden
                              </button>
                              <button
                                onClick={() => navigator.clipboard.writeText(`${window.location.origin}/view/${video.token}`)}
                                style={{
                                  flex: '1 1 auto',
                                  minWidth: '120px',
                                  padding: '8px 12px',
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                }}
                              >
                                Link kopieren
                              </button>
                              <button
                                onClick={() => setCurrentCardId(null)}
                                style={{
                                  flex: '1 1 auto',
                                  minWidth: '120px',
                                  padding: '8px 12px',
                                  background: '#6b7280',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                }}
                              >
                                Schliessen
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ padding: '20px' }}>
          <h3 style={{ color: '#a855f7', marginBottom: '20px' }}>Deine Video-Postkarte</h3>
          <VideoAccessCard
            qrId="main"
            qrUrl={qrData.url}
            video={qrData}
            senderName={user.email?.split('@')[0] || 'admin'}
          />

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px' }}>
            <button
              onClick={() => {
                setCurrentCardId('main');
                downloadCard('main');
              }}
              style={{
                padding: '10px 20px',
                background: '#a855f7',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              Postkarte downloaden
            </button>
            <button
              onClick={() => navigator.clipboard.writeText(qrData.url)}
              style={{
                padding: '10px 20px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              Link kopieren
            </button>
            <button
              onClick={() => setQrData(null)}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                color: '#a855f7',
                border: '1px solid rgba(168, 85, 247, 0.5)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              Schliessen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Videos;
